import { u } from '../shared/hardcodedKoEn';
/** CME Globex session for MES/MNQ/MGC/ZN (Sun 18:00 – Fri 17:00 ET, daily halt 17:00–18:00 ET). */

export type GlobexSessionState = 'open' | 'halt' | 'closed';

const WEEKDAY_ET: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

/** Post-rebalance refresh delay (systemd run + IBKR connect buffer). */
export const REBALANCE_REFRESH_DELAY_MS = 2 * 60 * 1000;

const REBALANCE_DAYS_ET = new Set([0, 1, 2, 3, 4]); // Sun–Thu

export function getEtClock(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? 'Mon';
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return { weekday, weekdayNum: WEEKDAY_ET[weekday] ?? 0, hour, minute };
}

/** Next Sun–Thu 18:15 ET rebalance (mercury-swing.timer). */
export function getNextRebalanceAt(now = new Date()): Date | null {
  for (let i = 1; i <= 7 * 24 * 4; i += 1) {
    const candidate = new Date(now.getTime() + i * 15 * 60 * 1000);
    const { weekdayNum, hour, minute } = getEtClock(candidate);
    if (REBALANCE_DAYS_ET.has(weekdayNum) && hour === 18 && minute === 15) {
      return candidate;
    }
  }
  return null;
}

export function parseRebalanceKst(value?: string | null): Date | null {
  if (!value) return null;
  const normalized = value.trim().replace(' ', 'T');
  const parsed = new Date(`${normalized}+09:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function scheduleRebalanceRefreshAt(
  nextRunKst: string | null | undefined,
  now = new Date(),
): Date | null {
  const fromApi = parseRebalanceKst(nextRunKst);
  const target = fromApi && fromApi > now ? fromApi : getNextRebalanceAt(now);
  if (!target) return null;
  return new Date(target.getTime() + REBALANCE_REFRESH_DELAY_MS);
}

export function getGlobexSessionState(now = new Date()): GlobexSessionState {
  const { weekdayNum, hour, minute } = getEtClock(now);
  const mins = hour * 60 + minute;
  const haltStart = 17 * 60;
  const haltEnd = 18 * 60;

  if (weekdayNum === 6) return 'closed';
  if (weekdayNum === 0) return mins >= haltEnd ? 'open' : 'closed';
  if (weekdayNum === 5) return mins < haltStart ? 'open' : 'closed';
  if (mins >= haltStart && mins < haltEnd) return 'halt';
  return 'open';
}

export function globexHoursLines() {
  return {
    line1: u('MES·MNQ·MGC·ZN 일봉 롱 · paper'),
    line2: u('일~목 18:15 ET 리밸런스'),
    line3: u('Globex 일 18:00–금 17:00 ET · 휴장 17–18 ET'),
  };
}

export function sessionStatusLabel(state: GlobexSessionState): string {
  switch (state) {
    case 'open': return 'OPEN';
    case 'halt': return 'HALT';
    default: return 'CLOSED';
  }
}

export function sessionStatusColor(state: GlobexSessionState): string {
  switch (state) {
    case 'open': return '#3fb950';
    case 'halt': return '#d29922';
    default: return '#c9a3a8';
  }
}
